import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from '../../../components/feature/Sidebar';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);


import { generatePDFReport, generateExcelReport } from '../../../utils/reportGenerator';
import {
    getMockAnimals,
    getMockMilkingData,
    getMockFeedData,
    getMockLocationData
} from '../../../data/farmtraceMockData';

type ViewType = 'overview' | 'production' | 'health' | 'efficiency' | 'analytics' | 'reports';

// ============= REPORTS VIEW =============
function ReportsView({ milkingData, metrics, animals }: any) {

    const handleDownload = (type: 'commercial' | 'technical' | 'health' | 'efficiency', format: 'pdf' | 'excel') => {
        const date = new Date().toLocaleDateString();

        let reportData;

        if (type === 'commercial') {
            // Preparar datos comerciales reales
            const commercialData = milkingData.map((m: any) => ({
                date: m.timestamp || m.date,
                concept: 'Venta de Leche Cruda',
                quantity: `${(m.milkYield || m.amount || 0).toFixed(1)} L`,
                unitPrice: metrics.avgMilkPrice,
                total: (m.milkYield || m.amount || 0) * metrics.avgMilkPrice
            }));

            reportData = {
                title: "Reporte Comercial Ejecutivo",
                subtitle: "Análisis de Ingresos y Rentabilidad",
                date,
                type: 'commercial',
                metrics: {
                    revenue: metrics.totalRevenue, // Use totalRevenue from metrics
                    margin: metrics.profitMargin,
                    costPerLiter: metrics.feedCostPerLiter,
                    roi: metrics.profitMargin // Assuming profitMargin can serve as ROI for this context
                },
                data: commercialData
            };
        } else if (type === 'technical') {
            // Preparar datos técnicos reales
            const technicalData = milkingData.map((m: any) => ({
                date: m.timestamp || m.date,
                animalId: m.animalId || 'N/A',
                event: 'Ordeño Automático',
                measurement: `${(m.milkYield || m.amount || 0).toFixed(1)} L`,
                status: (m.milkYield || 0) > 20 ? 'Óptimo' : 'Bajo'
            }));

            reportData = {
                title: "Reporte Técnico de Producción",
                subtitle: "Indicadores de Salud y Eficiencia del Hato",
                date,
                type: 'technical',
                metrics: {
                    totalProduction: metrics.dailyProduction * 7, // Assuming dailyProduction is for 1 day, multiply by 7 for total milk in 7 days
                    avgDaily: metrics.dailyProduction,
                    healthScore: metrics.healthScore,
                    efficiency: metrics.productionEfficiency
                },
                data: technicalData
            };
        } else if (type === 'health') {
            // Preparar datos de salud (simulados basados en lista de animales)
            const healthData = animals.map((a: any) => ({
                animalId: a.animalId || a.id || 'N/A',
                status: (Math.random() > 0.1) ? 'Saludable' : 'Requiere Atención',
                lastCheck: new Date().toISOString(),
                diagnosis: 'Control Rutinario',
                action: 'Ninguna'
            }));

            reportData = {
                title: "Reporte de Salud Veterinaria",
                subtitle: "Estado Sanitario y Tratamientos del Hato",
                date,
                type: 'health',
                metrics: {
                    healthScore: metrics.healthScore,
                    totalAnimals: animals.length,
                    inTreatment: 3, // Simulado
                    alerts: 1 // Simulado
                },
                data: healthData
            };
        } else if (type === 'efficiency') {
            // Preparar datos de eficiencia
            const efficiencyData = [
                { indicator: 'Producción Diaria Promedio', currentValue: `${metrics.dailyProduction.toFixed(1)} L`, targetValue: '3000 L', deviation: '+5%', status: 'Óptimo' },
                { indicator: 'Conversión Alimenticia', currentValue: '1.4', targetValue: '1.5', deviation: '-0.1', status: 'Excelente' },
                { indicator: 'Costo por Litro', currentValue: `$${metrics.feedCostPerLiter.toFixed(2)}`, targetValue: '$0.30', deviation: '+10%', status: 'Revisar' },
                { indicator: 'Margen Operativo', currentValue: `${metrics.profitMargin.toFixed(1)}%`, targetValue: '40%', deviation: '+2%', status: 'Bueno' }
            ];

            reportData = {
                title: "Reporte de Eficiencia Operativa",
                subtitle: "KPIs de Rendimiento y Costos",
                date,
                type: 'efficiency',
                metrics: {
                    efficiency: metrics.productionEfficiency,
                    costPerLiter: metrics.feedCostPerLiter,
                    profitMargin: metrics.profitMargin,
                    feedConversion: 1.4
                },
                data: efficiencyData
            };
        }

        if (format === 'pdf') {
            generatePDFReport(reportData as any);
        } else {
            generateExcelReport(reportData as any);
        }
    };

    return (
        <div className="space-y-6">
            <DashboardHero
                title="Centro de Reportes"
                subtitle="Genera informes corporativos detallados para la toma de decisiones."
                icon="ri-file-text-line"
                footerText="Documentación Oficial"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Reporte Comercial */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300 group">
                    <div className="h-32 bg-gradient-to-r from-blue-600 to-cyan-600 relative p-6 flex items-center justify-between">
                        <div className="text-white">
                            <h3 className="text-2xl font-bold">Reporte Comercial</h3>
                            <p className="opacity-90">Finanzas y Ventas</p>
                        </div>
                        <i className="ri-briefcase-4-line text-white text-5xl opacity-50 group-hover:scale-110 transition-transform"></i>
                    </div>
                    <div className="p-8">
                        <p className="text-gray-600 mb-6">
                            Informe detallado de ingresos, costos operativos, márgenes de utilidad y ROI. Ideal para presentaciones a gerencia y bancos.
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => handleDownload('commercial', 'pdf')}
                                className="w-full flex items-center justify-center space-x-2 bg-red-50 text-red-600 py-3 rounded-xl font-bold hover:bg-red-100 transition-colors border border-red-100"
                            >
                                <i className="ri-file-pdf-line text-xl"></i>
                                <span>Descargar PDF Corporativo</span>
                            </button>
                            <button
                                onClick={() => handleDownload('commercial', 'excel')}
                                className="w-full flex items-center justify-center space-x-2 bg-green-50 text-green-600 py-3 rounded-xl font-bold hover:bg-green-100 transition-colors border border-green-100"
                            >
                                <i className="ri-file-excel-line text-xl"></i>
                                <span>Exportar Excel</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Reporte Técnico */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300 group">
                    <div className="h-32 bg-gradient-to-r from-emerald-600 to-teal-600 relative p-6 flex items-center justify-between">
                        <div className="text-white">
                            <h3 className="text-2xl font-bold">Reporte Técnico</h3>
                            <p className="opacity-90">Producción y Salud</p>
                        </div>
                        <i className="ri-flask-line text-white text-5xl opacity-50 group-hover:scale-110 transition-transform"></i>
                    </div>
                    <div className="p-8">
                        <p className="text-gray-600 mb-6">
                            Análisis profundo de métricas de producción, curvas de lactancia, eventos de salud y eficiencia alimentaria por animal.
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => handleDownload('technical', 'pdf')}
                                className="w-full flex items-center justify-center space-x-2 bg-red-50 text-red-600 py-3 rounded-xl font-bold hover:bg-red-100 transition-colors border border-red-100"
                            >
                                <i className="ri-file-pdf-line text-xl"></i>
                                <span>Descargar PDF Técnico</span>
                            </button>
                            <button
                                onClick={() => handleDownload('technical', 'excel')}
                                className="w-full flex items-center justify-center space-x-2 bg-green-50 text-green-600 py-3 rounded-xl font-bold hover:bg-green-100 transition-colors border border-green-100"
                            >
                                <i className="ri-file-excel-line text-xl"></i>
                                <span>Exportar Excel</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Reporte de Salud */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300 group">
                    <div className="h-32 bg-gradient-to-r from-red-500 to-orange-500 relative p-6 flex items-center justify-between">
                        <div className="text-white">
                            <h3 className="text-2xl font-bold">Reporte de Salud</h3>
                            <p className="opacity-90">Veterinaria y Bienestar</p>
                        </div>
                        <i className="ri-heart-pulse-line text-white text-5xl opacity-50 group-hover:scale-110 transition-transform"></i>
                    </div>
                    <div className="p-8">
                        <p className="text-gray-600 mb-6">
                            Estado sanitario del hato, historial de tratamientos, alertas veterinarias y seguimiento de bienestar animal.
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => handleDownload('health', 'pdf')}
                                className="w-full flex items-center justify-center space-x-2 bg-red-50 text-red-600 py-3 rounded-xl font-bold hover:bg-red-100 transition-colors border border-red-100"
                            >
                                <i className="ri-file-pdf-line text-xl"></i>
                                <span>Descargar PDF Salud</span>
                            </button>
                            <button
                                onClick={() => handleDownload('health', 'excel')}
                                className="w-full flex items-center justify-center space-x-2 bg-green-50 text-green-600 py-3 rounded-xl font-bold hover:bg-green-100 transition-colors border border-green-100"
                            >
                                <i className="ri-file-excel-line text-xl"></i>
                                <span>Exportar Excel</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Reporte de Eficiencia */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300 group">
                    <div className="h-32 bg-gradient-to-r from-purple-600 to-indigo-600 relative p-6 flex items-center justify-between">
                        <div className="text-white">
                            <h3 className="text-2xl font-bold">Reporte Eficiencia</h3>
                            <p className="opacity-90">KPIs y Rendimiento</p>
                        </div>
                        <i className="ri-speed-line text-white text-5xl opacity-50 group-hover:scale-110 transition-transform"></i>
                    </div>
                    <div className="p-8">
                        <p className="text-gray-600 mb-6">
                            Análisis de conversión alimenticia, costos por litro, eficiencia operativa y comparativas con objetivos.
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => handleDownload('efficiency', 'pdf')}
                                className="w-full flex items-center justify-center space-x-2 bg-red-50 text-red-600 py-3 rounded-xl font-bold hover:bg-red-100 transition-colors border border-red-100"
                            >
                                <i className="ri-file-pdf-line text-xl"></i>
                                <span>Descargar PDF Eficiencia</span>
                            </button>
                            <button
                                onClick={() => handleDownload('efficiency', 'excel')}
                                className="w-full flex items-center justify-center space-x-2 bg-green-50 text-green-600 py-3 rounded-xl font-bold hover:bg-green-100 transition-colors border border-green-100"
                            >
                                <i className="ri-file-excel-line text-xl"></i>
                                <span>Exportar Excel</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function FarmTraceDashboard() {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedFarm] = useState('NL0000079');
    const [currentView, setCurrentView] = useState<ViewType>('overview');

    // Data states
    const [animals, setAnimals] = useState<any[]>([]);
    const [milkingData, setMilkingData] = useState<any[]>([]);
    const [feedData, setFeedData] = useState<any[]>([]);
    const [locationData, setLocationData] = useState<any>(null);

    // Business KPIs
    const [businessMetrics, setBusinessMetrics] = useState({
        totalRevenue: 0,
        avgMilkPrice: 0.35, // USD per liter (ejemplo)
        dailyProduction: 0,
        productionEfficiency: 0,
        feedCostPerLiter: 0,
        profitMargin: 0,
        healthScore: 0,
        productionTrend: 0,
    });

    useEffect(() => {
        const path = location.pathname;
        if (path.includes('/farmtrace/overview')) setCurrentView('overview');
        else if (path.includes('/farmtrace/production')) setCurrentView('production');
        else if (path.includes('/farmtrace/health')) setCurrentView('health');
        else if (path.includes('/farmtrace/efficiency')) setCurrentView('efficiency');
        else if (path.includes('/farmtrace/reports')) setCurrentView('reports');
        else if (path.includes('/farmtrace/analytics')) setCurrentView('analytics');
        else setCurrentView('overview');
    }, [location]);

    useEffect(() => {
        loadDashboardData();
    }, [selectedFarm]);

    const extractArray = (response: any): any[] => {
        if (Array.isArray(response)) return response;
        if (response?.items) return response.items;
        if (response?.member) return response.member;
        if (response?.data) return response.data;
        return [];
    };

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [locationsRes, animalsRes, milkingRes, feedRes] = await Promise.allSettled([
                getMockLocationData().then(data => [data]),
                getMockAnimals(),
                getMockMilkingData(7),
                getMockFeedData(7),
            ]);

            const locations = locationsRes.status === 'fulfilled' ? extractArray(locationsRes.value) : [];
            const animalsData = animalsRes.status === 'fulfilled' ? extractArray(animalsRes.value) : [];
            const milkingVisits = milkingRes.status === 'fulfilled' ? extractArray(milkingRes.value) : [];
            const feedIntakes = feedRes.status === 'fulfilled' ? extractArray(feedRes.value) : [];

            setLocationData(locations[0] || null);
            setAnimals(animalsData);
            setMilkingData(milkingVisits);
            setFeedData(feedIntakes);

            // Calculate business metrics
            const totalMilk = milkingVisits.reduce((sum, visit) => {
                const amount = visit.milkYield || visit.yield || visit.amount || 0;
                return sum + amount;
            }, 0);

            const totalFeed = feedIntakes.reduce((sum, feed) => {
                const amount = feed.feedIntake || feed.amount || feed.quantity || 0;
                return sum + amount;
            }, 0);

            const avgMilkPrice = 0.35; // USD per liter
            const avgFeedCost = 0.40; // USD per kg
            const dailyProduction = milkingVisits.length > 0 ? totalMilk / 7 : 0;
            const revenue = totalMilk * avgMilkPrice;
            const feedCost = totalFeed * avgFeedCost;
            const feedCostPerLiter = totalMilk > 0 ? feedCost / totalMilk : 0;
            const profitMargin = revenue > 0 ? ((revenue - feedCost) / revenue) * 100 : 0;
            const targetPerCow = 28; // Objetivo de litros por vaca
            const avgProductionPerCow = animalsData.length > 0 ? dailyProduction / animalsData.length : 0;
            const productionEfficiency = (avgProductionPerCow / targetPerCow) * 100;

            // Health score (simulado basado en datos disponibles)
            const healthScore = 85 + Math.random() * 10;

            // Production trend (comparación simulada)
            const productionTrend = 5 + Math.random() * 10;

            setBusinessMetrics({
                totalRevenue: revenue,
                avgMilkPrice,
                dailyProduction,
                productionEfficiency,
                feedCostPerLiter,
                profitMargin,
                healthScore,
                productionTrend,
            });

        } catch (err) {
            console.error('Error loading dashboard:', err);
            setError(err instanceof Error ? err.message : 'Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };


    const viewTitles: Record<ViewType, string> = {
        overview: 'Resumen Ejecutivo',
        production: 'Producción de Leche',
        health: 'Salud del Ganado',
        efficiency: 'Eficiencia',
        analytics: 'Análisis Avanzado',
        reports: 'Reportes'
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-700 text-lg font-medium">Cargando datos de FarmTrace...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
                <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-error-warning-line text-red-500 text-3xl"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Error de conexión</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <div className="space-y-3">
                        <button
                            onClick={loadDashboardData}
                            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold py-3 rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-300"
                        >
                            Reintentar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex">
            {/* Sidebar */}
            <Sidebar />

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden ml-[var(--sidebar-width)] transition-all duration-300">
                {/* Top Bar */}
                <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200 px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                {viewTitles[currentView]}
                            </h1>
                            <p className="text-sm text-gray-600 mt-1">
                                {locationData?.name || `Granja: ${selectedFarm}`}
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="text-right mr-4">
                                <p className="text-xs text-gray-600">Última actualización</p>
                                <p className="text-sm font-semibold text-gray-900">
                                    {new Date().toLocaleString('es-ES', {
                                        day: '2-digit',
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </p>
                            </div>
                            <button
                                onClick={loadDashboardData}
                                className="p-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-lg"
                                title="Actualizar datos"
                            >
                                <i className="ri-refresh-line text-lg"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-8">
                    {currentView === 'overview' && (
                        <OverviewView
                            animals={animals}
                            milkingData={milkingData}
                            feedData={feedData}
                            metrics={businessMetrics}
                        />
                    )}
                    {currentView === 'production' && (
                        <ProductionView milkingData={milkingData} metrics={businessMetrics} />
                    )}
                    {currentView === 'health' && <HealthView animals={animals} metrics={businessMetrics} />}
                    {currentView === 'efficiency' && (
                        <EfficiencyView
                            animals={animals}
                            milkingData={milkingData}
                            feedData={feedData}
                            metrics={businessMetrics}
                        />
                    )}
                    {currentView === 'analytics' && (
                        <AnalyticsView
                            animals={animals}
                            milkingData={milkingData}
                            feedData={feedData}
                            metrics={businessMetrics}
                        />
                    )}
                    {currentView === 'reports' && (
                        <ReportsView
                            milkingData={milkingData}
                            metrics={businessMetrics}
                            animals={animals}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

// ============= REUSABLE COMPONENTS =============
const DashboardHero = ({ title, subtitle, icon = "ri-leaf-line", footerText = "FarmTrace te mantiene conectado." }: any) => (
    <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 overflow-hidden shadow-2xl mb-6">
        <img
            src="/images/farmtrace/dashboard_hero.png"
            alt="Farm background"
            className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative z-10 text-white">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-4 animate-fade-in-up">
                {title}
            </h1>
            <p className="text-lg md:text-xl font-light opacity-90 animate-fade-in-up animation-delay-200">
                {subtitle}
            </p>
            <div className="mt-6 flex items-center space-x-3 animate-fade-in-up animation-delay-400">
                <i className={`${icon} text-3xl text-green-300`}></i>
                <span className="text-xl font-semibold">{footerText}</span>
            </div>
        </div>
    </div>
);

// ============= OVERVIEW VIEW =============
function OverviewView({ animals, milkingData, feedData, metrics }: any) {
    // Prepare data for the unified chart
    const chartLabels = milkingData.slice(0, 7).map((d: any) => new Date(d.date || d.timestamp).toLocaleDateString('es-ES', { weekday: 'short' })).reverse();
    const milkProductionData = milkingData.slice(0, 7).map((d: any) => d.milkYield || d.amount || 0).reverse();
    // Simulate energy data correlated with production for demo purposes
    const energyConsumptionData = milkProductionData.map((yieldVal: number) => yieldVal * 0.12); // ~0.12 kWh per liter

    const chartData = {
        labels: chartLabels,
        datasets: [
            {
                label: 'Producción (L)',
                data: milkProductionData,
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                yAxisID: 'y',
                tension: 0.4,
                fill: true,
            },
            {
                label: 'Energía (kWh)',
                data: energyConsumptionData,
                borderColor: 'rgb(34, 197, 94)',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                yAxisID: 'y1',
                tension: 0.4,
                borderDash: [5, 5],
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: false,
            },
        },
        scales: {
            y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                title: { display: true, text: 'Litros' }
            },
            y1: {
                type: 'linear' as const,
                display: true,
                position: 'right' as const,
                grid: {
                    drawOnChartArea: false,
                },
                title: { display: true, text: 'kWh' }
            },
        },
    };

    return (
        <div className="space-y-8">
            {/* Hero Section */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 p-8 md:p-12 shadow-2xl">
                {/* Background Image */}
                <img
                    src="/images/farmtrace/dashboard_hero.png"
                    alt="Farm background"
                    className="absolute inset-0 w-full h-full object-cover opacity-20"
                />

                {/* Decorative blurs */}
                <div className="absolute top-0 right-0 -mt-10 -mr-10 h-64 w-64 rounded-full bg-yellow-400 opacity-20 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 -mb-10 -ml-10 h-64 w-64 rounded-full bg-blue-400 opacity-20 blur-3xl"></div>

                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center">
                    <div>
                        <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3 drop-shadow-lg">
                            Hola, Campos Smart 👋
                        </h1>
                        <p className="text-green-50 text-lg md:text-xl font-light">
                            Tu sistema opera con <span className="font-bold text-yellow-300">Alta Eficiencia</span> hoy.
                        </p>
                    </div>
                    <div className="mt-6 md:mt-0 flex flex-col sm:flex-row gap-4">
                        <div className="bg-white/20 backdrop-blur-md rounded-xl p-4 border border-white/20 shadow-lg hover:bg-white/25 transition-all duration-300">
                            <p className="text-xs text-green-100 uppercase tracking-wider font-semibold">Estado Solar</p>
                            <div className="flex items-center gap-2 mt-2">
                                <span className="flex h-3 w-3 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400"></span>
                                </span>
                                <span className="text-white font-bold text-sm">Generando 45kW</span>
                            </div>
                        </div>
                        <div className="bg-white/20 backdrop-blur-md rounded-xl p-4 border border-white/20 shadow-lg hover:bg-white/25 transition-all duration-300">
                            <p className="text-xs text-green-100 uppercase tracking-wider font-semibold">Estado Granja</p>
                            <div className="flex items-center gap-2 mt-2">
                                <span className="flex h-3 w-3 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-300 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-400"></span>
                                </span>
                                <span className="text-white font-bold text-sm">Ordeño en curso</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* KPI Cards - Integrated Vision */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Costo Energético por Litro */}
                <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-3 bg-yellow-50 rounded-xl group-hover:bg-yellow-100 transition-colors">
                            <i className="ri-flashlight-fill text-yellow-600 text-xl"></i>
                        </div>
                        <span className="px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-100">
                            -15% vs Red
                        </span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-1">$0.012</h3>
                    <p className="text-sm text-gray-500 font-medium">Costo Energético / Litro</p>
                    <div className="mt-4">
                        <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">Solar vs Red</span>
                            <span className="text-gray-900 font-medium">45% Solar</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 h-full rounded-full" style={{ width: '45%' }}></div>
                        </div>
                    </div>
                </div>

                {/* Huella de Carbono */}
                <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-3 bg-green-50 rounded-xl group-hover:bg-green-100 transition-colors">
                            <i className="ri-leaf-fill text-green-600 text-xl"></i>
                        </div>
                        <span className="px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-100">
                            Sostenible
                        </span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-1">0.85 <span className="text-lg text-gray-400 font-normal">kg</span></h3>
                    <p className="text-sm text-gray-500 font-medium">CO₂ por Litro</p>
                    <div className="mt-4 flex items-center gap-2 text-xs text-green-600 bg-green-50 p-2 rounded-lg">
                        <i className="ri-arrow-down-line"></i>
                        <span>Ahorro mensual: 1.2 Ton CO₂</span>
                    </div>
                </div>

                {/* Calidad de Leche */}
                <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors">
                            <i className="ri-drop-fill text-blue-600 text-xl"></i>
                        </div>
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-100">
                            Premium
                        </span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-1">98.5%</h3>
                    <p className="text-sm text-gray-500 font-medium">Score de Calidad</p>
                    <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                        <i className="ri-check-double-line text-green-500"></i>
                        <span>Cadena de frío 100% Solar</span>
                    </div>
                </div>

                {/* Eficiencia Combinada */}
                <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-3 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-colors">
                            <i className="ri-pulse-fill text-purple-600 text-xl"></i>
                        </div>
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full border border-purple-100">
                            Alta
                        </span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-1">2.4 <span className="text-lg text-gray-400 font-normal">L/kWh</span></h3>
                    <p className="text-sm text-gray-500 font-medium">Rendimiento Energético</p>
                    <div className="mt-4 text-xs text-gray-400">
                        Litros producidos por kWh consumido
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-8">
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">

                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Eficiencia Energética de Producción</h2>
                            <p className="text-sm text-gray-500">Correlación entre litros producidos y consumo energético</p>
                        </div>
                        <button className="p-2 hover:bg-gray-50 rounded-lg transition-colors text-gray-400 hover:text-gray-600">
                            <i className="ri-more-2-fill text-xl"></i>
                        </button>
                    </div>

                    {/* Contenedor con altura real y sin estirar */}
                    <div className="relative h-72 md:h-80">
                        <Line options={{
                            ...chartOptions,
                            maintainAspectRatio: false,
                            layout: {
                                padding: {
                                    top: 10,
                                    bottom: 0,   // <-- evita el espacio extra
                                }
                            }
                        }} data={chartData} />
                    </div>

                </div>
            </div>


        </div>
    );
}

// ============= PRODUCTION VIEW =============
function ProductionView({ milkingData, metrics }: any) {
    return (
        <div className="space-y-6">
            <DashboardHero
                title="Producción de Leche"
                subtitle="Análisis detallado de tu producción diaria y tendencias."
                icon="ri-drop-line"
                footerText="Calidad Premium"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Producción Total</h3>
                    <p className="text-4xl font-bold text-blue-600">
                        {milkingData
                            .reduce((s: number, v: any) => s + (v.milkYield || v.yield || v.amount || 0), 0)
                            .toFixed(0)}
                        L
                    </p>
                    <p className="text-sm text-gray-600 mt-2">Últimos 7 días</p>
                </div>

                <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Precio Promedio</h3>
                    <p className="text-4xl font-bold text-green-600">${metrics.avgMilkPrice.toFixed(2)}</p>
                    <p className="text-sm text-gray-600 mt-2">Por litro</p>
                </div>

                <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Visitas de Ordeño</h3>
                    <p className="text-4xl font-bold text-purple-600">{milkingData.length}</p>
                    <p className="text-sm text-gray-600 mt-2">Últimos 7 días</p>
                </div>
            </div>

            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Detalle de Producción</h2>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4 font-semibold text-gray-700">Fecha</th>
                                <th className="text-left py-3 px-4 font-semibold text-gray-700">Animal</th>
                                <th className="text-right py-3 px-4 font-semibold text-gray-700">Producción</th>
                                <th className="text-right py-3 px-4 font-semibold text-gray-700">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {milkingData.slice(0, 15).map((visit: any, index: number) => {
                                const amount = visit.milkYield || visit.yield || visit.amount || 0;
                                const timestamp =
                                    visit.timestamp || visit.date || visit.datetime || new Date().toISOString();
                                const animalId = visit.animalId || visit.cowId || `#${index + 1}`;
                                const value = amount * metrics.avgMilkPrice;

                                return (
                                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-3 px-4 text-sm text-gray-700">
                                            {new Date(timestamp).toLocaleDateString('es-ES', {
                                                day: '2-digit',
                                                month: 'short',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })}
                                        </td>
                                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{animalId}</td>
                                        <td className="py-3 px-4 text-sm text-right font-semibold text-blue-600">
                                            {amount.toFixed(1)}L
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right font-semibold text-green-600">
                                            ${value.toFixed(2)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ============= HEALTH VIEW =============
function HealthView({ animals }: any) {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedAnimal, setSelectedAnimal] = useState<any>(null);
    const itemsPerPage = 10;

    // Pagination Logic
    const totalPages = Math.ceil(animals.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentAnimals = animals.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        // Scroll to top of grid smoothly
        document.getElementById('health-grid')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="space-y-6" id="health-grid">
            <DashboardHero
                title="Salud del Ganado"
                subtitle="Monitoreo en tiempo real del bienestar de tu hato."
                icon="ri-heart-pulse-line"
                footerText="Bienestar Animal"
            />
            {/* Health Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-500">Índice de Salud</p>
                        <p className="text-2xl font-bold text-green-600">98%</p>
                    </div>
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <i className="ri-heart-pulse-line text-green-600 text-xl"></i>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-500">Total Ganado</p>
                        <p className="text-2xl font-bold text-blue-600">{animals.length}</p>
                    </div>
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-cow-line text-blue-600 text-xl"></i>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-500">En Tratamiento</p>
                        <p className="text-2xl font-bold text-orange-600">3</p>
                    </div>
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <i className="ri-hospital-line text-orange-600 text-xl"></i>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-500">Alertas</p>
                        <p className="text-2xl font-bold text-red-600">1</p>
                    </div>
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <i className="ri-alarm-warning-line text-red-600 text-xl"></i>
                    </div>
                </div>
            </div>

            {/* Animals Grid */}
            <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">Estado del Ganado</h2>
                    <span className="text-sm text-gray-500">Mostrando {startIndex + 1}-{Math.min(startIndex + itemsPerPage, animals.length)} de {animals.length}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    {currentAnimals.map((animal: any, index: number) => {
                        const animalId = animal.animalId || animal.id || `Animal ${startIndex + index + 1}`;
                        // Simulate health status randomly for demo
                        const isHealthy = Math.random() > 0.1;
                        const healthStatus = isHealthy ? 'Saludable' : 'Requiere Atención';
                        const lastCheck = new Date().toLocaleDateString();

                        return (
                            <div
                                key={index}
                                onClick={() => setSelectedAnimal({ ...animal, animalId, isHealthy, healthStatus, lastCheck })}
                                className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-4 cursor-pointer group border border-transparent hover:border-blue-200 flex flex-col items-center text-center relative overflow-hidden"
                            >
                                <div className={`absolute top-0 left-0 w-full h-1 ${isHealthy ? 'bg-green-500' : 'bg-yellow-500'}`}></div>

                                <div className="relative mb-3 mt-2">
                                    <img
                                        src="/images/farmtrace/cow_avatar.png"
                                        alt="Cow"
                                        className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md group-hover:scale-105 transition-transform duration-300"
                                    />
                                    <div className={`absolute bottom-0 right-0 w-6 h-6 ${isHealthy ? 'bg-green-500' : 'bg-yellow-500'} rounded-full flex items-center justify-center border-2 border-white`}>
                                        <i className={`${isHealthy ? 'ri-check-line' : 'ri-alert-line'} text-white text-xs`}></i>
                                    </div>
                                </div>

                                <h3 className="font-bold text-gray-900 text-lg mb-1 group-hover:text-blue-600 transition-colors">{animalId}</h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium mb-3 ${isHealthy ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                    {healthStatus}
                                </span>

                                <div className="w-full grid grid-cols-2 gap-2 text-xs text-gray-500 mt-2 pt-3 border-t border-gray-100">
                                    <div>
                                        <p className="mb-1">Último Control</p>
                                        <p className="font-semibold text-gray-700">{lastCheck}</p>
                                    </div>
                                    <div>
                                        <p className="mb-1">Producción</p>
                                        <p className="font-semibold text-gray-700">28.5 L</p>
                                    </div>
                                </div>

                                <button className="mt-4 w-full py-2 bg-gray-50 text-blue-600 rounded-lg text-sm font-medium group-hover:bg-blue-50 transition-colors">
                                    Ver Ficha
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="flex justify-center items-center space-x-2 mt-8">
                        <button
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <i className="ri-arrow-left-s-line"></i>
                        </button>

                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            // Logic to show window of pages around current page
                            let pageNum = i + 1;
                            if (totalPages > 5) {
                                if (currentPage > 3) {
                                    pageNum = currentPage - 2 + i;
                                }
                                if (pageNum > totalPages) {
                                    pageNum = totalPages - 4 + i;
                                }
                            }

                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`w-10 h-10 flex items-center justify-center rounded-full font-medium transition-all duration-200 ${currentPage === pageNum
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-110'
                                        : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}

                        <button
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <i className="ri-arrow-right-s-line"></i>
                        </button>
                    </div>
                )}
            </div>

            {/* Animal Detail Modal */}
            {selectedAnimal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-slide-up">
                        {/* Modal Header */}
                        <div className="relative h-32 bg-gradient-to-r from-blue-600 to-indigo-700">
                            <button
                                onClick={() => setSelectedAnimal(null)}
                                className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
                            >
                                <i className="ri-close-line text-xl"></i>
                            </button>
                            <div className="absolute -bottom-12 left-8 flex items-end">
                                <img
                                    src="/images/farmtrace/cow_avatar.png"
                                    alt="Cow"
                                    className="w-24 h-24 rounded-full border-4 border-white shadow-lg bg-white"
                                />
                                <div className="mb-12 ml-4 text-white">
                                    <h2 className="text-2xl font-bold">{selectedAnimal.animalId}</h2>
                                    <p className="opacity-90 text-sm">Registro: #883920</p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="pt-16 pb-8 px-8">
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedAnimal.isHealthy ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        <span className={`w-2 h-2 rounded-full mr-2 ${selectedAnimal.isHealthy ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                                        {selectedAnimal.healthStatus}
                                    </span>
                                </div>
                                <div className="flex space-x-2">
                                    <button className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
                                        <i className="ri-file-list-line mr-2"></i>Historial
                                    </button>
                                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200">
                                        <i className="ri-edit-line mr-2"></i>Editar
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Datos Generales</h4>
                                    <div className="flex justify-between border-b border-gray-100 pb-2">
                                        <span className="text-gray-600">Edad</span>
                                        <span className="font-semibold text-gray-900">3.5 Años</span>
                                    </div>
                                    <div className="flex justify-between border-b border-gray-100 pb-2">
                                        <span className="text-gray-600">Peso</span>
                                        <span className="font-semibold text-gray-900">650 kg</span>
                                    </div>
                                    <div className="flex justify-between border-b border-gray-100 pb-2">
                                        <span className="text-gray-600">Lactancia</span>
                                        <span className="font-semibold text-gray-900">2da</span>
                                    </div>
                                    <div className="flex justify-between border-b border-gray-100 pb-2">
                                        <span className="text-gray-600">Grupo</span>
                                        <span className="font-semibold text-gray-900">Producción Alta</span>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Producción (Hoy)</h4>
                                    <div className="bg-blue-50 rounded-xl p-4 flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-blue-600 mb-1">Mañana</p>
                                            <p className="text-xl font-bold text-blue-900">14.2 L</p>
                                        </div>
                                        <i className="ri-sun-line text-blue-300 text-2xl"></i>
                                    </div>
                                    <div className="bg-indigo-50 rounded-xl p-4 flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-indigo-600 mb-1">Tarde</p>
                                            <p className="text-xl font-bold text-indigo-900">14.3 L</p>
                                        </div>
                                        <i className="ri-moon-line text-indigo-300 text-2xl"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============= EFFICIENCY VIEW =============
function EfficiencyView({ metrics }: any) {
    return (
        <div className="space-y-6">
            <DashboardHero
                title="Eficiencia Operativa"
                subtitle="Optimiza tus recursos y maximiza tu rentabilidad."
                icon="ri-speed-line"
                footerText="Alto Rendimiento"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Eficiencia de Producción</h3>
                    <p className="text-4xl font-bold text-blue-600">
                        {metrics.productionEfficiency.toFixed(0)}%
                    </p>
                    <p className="text-sm text-gray-600 mt-2">Sobre objetivo</p>
                </div>

                <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Costo por Litro</h3>
                    <p className="text-4xl font-bold text-orange-600">
                        ${metrics.feedCostPerLiter.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">Alimento</p>
                </div>

                <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">ROI</h3>
                    <p className="text-4xl font-bold text-green-600">{metrics.profitMargin.toFixed(0)}%</p>
                    <p className="text-sm text-gray-600 mt-2">Retorno de inversión</p>
                </div>
            </div>

            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Análisis de Eficiencia</h2>
                <div className="space-y-4">
                    <div>
                        <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Producción vs Objetivo</span>
                            <span className="text-sm font-semibold text-green-600">
                                {metrics.productionEfficiency.toFixed(0)}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full"
                                style={{ width: `${Math.min(metrics.productionEfficiency, 100)}%` }}
                            ></div>
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Rentabilidad</span>
                            <span className="text-sm font-semibold text-blue-600">
                                {metrics.profitMargin.toFixed(0)}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full"
                                style={{ width: `${Math.min(metrics.profitMargin, 100)}%` }}
                            ></div>
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Salud del Ganado</span>
                            <span className="text-sm font-semibold text-purple-600">
                                {metrics.healthScore.toFixed(0)}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full"
                                style={{ width: `${Math.min(metrics.healthScore, 100)}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============= ANALYTICS VIEW =============
function AnalyticsView({ metrics }: any) {
    return (
        <div className="space-y-6">
            <DashboardHero
                title="Análisis Avanzado"
                subtitle="Proyecciones inteligentes para tomar mejores decisiones."
                icon="ri-line-chart-line"
                footerText="Insights con IA"
            />
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Tendencias y Proyecciones</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                        <h3 className="font-bold text-gray-900 mb-2">Proyección Mensual</h3>
                        <p className="text-3xl font-bold text-blue-600">
                            ${(metrics.totalRevenue * 4.3).toFixed(0)}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Ingresos estimados</p>
                    </div>

                    <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
                        <h3 className="font-bold text-gray-900 mb-2">Crecimiento</h3>
                        <p className="text-3xl font-bold text-green-600">+{metrics.productionTrend.toFixed(1)}%</p>
                        <p className="text-sm text-gray-600 mt-1">vs mes anterior</p>
                    </div>
                </div>
            </div>

            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Insights Inteligentes</h2>
                <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-xl border-l-4 border-blue-500">
                        <p className="font-semibold text-gray-900 mb-1">💡 Optimización Detectada</p>
                        <p className="text-sm text-gray-700">
                            Tu producción promedio por animal está {metrics.productionTrend.toFixed(1)}% por encima
                            del benchmark de la industria.
                        </p>
                    </div>

                    <div className="p-4 bg-green-50 rounded-xl border-l-4 border-green-500">
                        <p className="font-semibold text-gray-900 mb-1">📈 Tendencia Positiva</p>
                        <p className="text-sm text-gray-700">
                            Los últimos 7 días muestran un incremento constante en la producción diaria.
                        </p>
                    </div>

                    <div className="p-4 bg-purple-50 rounded-xl border-l-4 border-purple-500">
                        <p className="font-semibold text-gray-900 mb-1">🎯 Recomendación</p>
                        <p className="text-sm text-gray-700">
                            Considera aumentar el objetivo de producción un 5% para maximizar la rentabilidad.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
